import React from 'react';
import { siError, useLearnerRanking } from '../../hooks/useStudentRankings';
import {
  Breakdown,
  KindBadge,
  percent,
  score,
  updatedText,
} from './rankingText';

// The learner's rank in a cohort (theirs by default), overall and per module,
// with the breakdown so "why this rank?" is answerable.
export default function LearnerRanking({ studentId, cohortId }) {
  const { data, isLoading, error } = useLearnerRanking(studentId, cohortId);

  if (isLoading)
    return (
      <div
        className="h-48 rounded-xl bg-gray-100 animate-pulse"
        aria-label="Loading"
      />
    );
  if (error) {
    return (
      <p className="text-sm text-gray-600">
        {siError(error, "We couldn't load this learner's ranking.")}
      </p>
    );
  }
  const row = data?.student;
  if (!row)
    return (
      <p className="text-sm text-gray-500">No ranking for this learner.</p>
    );
  const modules = data.modules ?? [];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[16rem_1fr]">
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-5 space-y-1">
          <p className="text-xs uppercase tracking-wide text-gray-500">
            {data.cohort?.name || 'Cohort'}
          </p>
          <p className="text-3xl font-bold text-gray-900">
            {row.rank}
            <span className="text-base font-medium text-gray-500">
              {' '}
              of {(data.of ?? 0).toLocaleString()}
            </span>
          </p>
          <p className="text-sm text-gray-700">Score {score(row.score)}</p>
          {data.generated_at && (
            <p className="text-[11px] text-gray-400">
              {updatedText(data.generated_at)}
            </p>
          )}
        </div>
        <div className="rounded-xl border border-gray-100 bg-white p-5">
          <Breakdown
            components={row.components}
            details={row.details}
            weights={data.weights}
          />
        </div>
      </div>

      {modules.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="py-2 pl-4 pr-2 font-medium">Module</th>
                <th className="px-2 py-2 text-right font-medium">Rank</th>
                <th className="px-2 py-2 text-right font-medium">Score</th>
                <th className="px-2 py-2 text-right font-medium">Quizzes</th>
                <th className="px-2 py-2 text-right font-medium">
                  Assignments
                </th>
                <th className="py-2 pl-2 pr-4 text-right font-medium">
                  Content
                </th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr
                  key={m.module_id}
                  className="border-b border-gray-100 last:border-0"
                >
                  <td className="py-2 pl-4 pr-2">
                    <span className="text-gray-900">{m.name}</span>{' '}
                    <KindBadge kind={m.kind} />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-900">
                    {m.rank ?? '–'}
                    {m.of ? (
                      <span className="text-gray-400">
                        {' '}
                        of {m.of.toLocaleString()}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2 py-2 text-right font-medium tabular-nums">
                    {score(m.score)}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                    {percent(m.components?.quizzes)}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                    {percent(m.components?.assignments)}
                  </td>
                  <td className="py-2 pl-2 pr-4 text-right tabular-nums text-gray-700">
                    {percent(m.components?.content)}
                    {m.details?.weeks_total ? (
                      <span className="block text-[11px] text-gray-400">
                        {m.details.weeks_done ?? 0}/{m.details.weeks_total}{' '}
                        weeks
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
